import { webLogger } from '@muse-web/logger'

import { getNetworkErrorKind, type TNetworkErrorKind } from './getNetworkErrorKind'

// MI2-2025 migration shim. audio-web used a Sentry-adapter `Logger` with a different surface than
// `@muse-web/logger` (error(msg, extra) / info / setUser / removeUser). This adapts those call sites
// onto webLogger so the migrated data-layer hooks keep working verbatim. It adds real logic (signature
// adaptation + extras serialization), so it is not a thin pass-through wrapper.
// setUser/removeUser are no-ops for now — attaching user context to the logger is a separate concern
// from the data-layer port and will be wired with the auth track.

type TLoggerUser = {
    id: string
    email?: string
    username?: string
}

type TLoggerErrorExtras = Record<string, unknown>

// JSON.stringify turns Error values into "{}" — serialize them explicitly so error details
// nested in extras (a common call shape) survive into the log line.
const serializeExtras = (extras: TLoggerErrorExtras): string =>
    JSON.stringify(extras, (_key, value) =>
        value instanceof Error
            ? { name: value.name, message: value.message, stack: value.stack }
            : value,
    )

const withExtras = (message: string, extras?: TLoggerErrorExtras): string => {
    if (!extras || Object.keys(extras).length === 0) return message
    try {
        return `${message} ${serializeExtras(extras)}`
    } catch {
        return message
    }
}

// Current logger user context. Tracked here now; attaching it to the underlying error transport
// (Sentry) is wired with the auth track. Exported so consumers/tests can read the active context.
export const loggerContext: { user: TLoggerUser | null } = { user: null }

type TSentryLevel = 'error' | 'warning'

type TSentryHint = {
    captureContext: {
        level: TSentryLevel
        tags: Record<string, string>
        fingerprint: string[]
    }
}

type TSentryHub = {
    captureException: (error: Error, hint: TSentryHint) => unknown
    captureMessage: (message: string, level: TSentryLevel, hint: TSentryHint) => unknown
}

const isSentryHub = (value: unknown): value is TSentryHub =>
    typeof value === 'object' &&
    value !== null &&
    Reflect.get(value, '_lazy') === undefined &&
    'captureException' in value &&
    typeof value.captureException === 'function' &&
    'captureMessage' in value &&
    typeof value.captureMessage === 'function'

const getSentryHub = (): TSentryHub | null => {
    if (__DEV__ || typeof window === 'undefined') {
        return null
    }

    const sentry: unknown = Reflect.get(window, 'Sentry')

    return isSentryHub(sentry) ? sentry : null
}

const report = (value: Error | string, networkErrorKind: TNetworkErrorKind | null): void => {
    const hub = networkErrorKind ? getSentryHub() : null

    if (!hub || !networkErrorKind) {
        webLogger.error(value)

        return
    }

    const hint: TSentryHint = {
        captureContext: {
            level: networkErrorKind === 'timeout' ? 'error' : 'warning',
            tags: { error_kind: networkErrorKind },
            fingerprint: [`${networkErrorKind}-error`],
        },
    }

    if (typeof value === 'string') {
        hub.captureMessage(value, hint.captureContext.level, hint)

        return
    }

    hub.captureException(value, hint)
}

export const Logger = {
    error: (error: Error | string, extras?: TLoggerErrorExtras): void => {
        const networkErrorKind = getNetworkErrorKind(error) ?? getNetworkErrorKind(extras?.error)

        if (typeof error === 'string') {
            report(withExtras(error, extras), networkErrorKind)

            return
        }

        // webLogger.error only accepts string | Error (no structured-context channel), so when
        // extras accompany an Error, fold them into the message but keep the original stack.
        if (!extras || Object.keys(extras).length === 0) {
            report(error, networkErrorKind)

            return
        }

        const enriched = new Error(withExtras(error.message, extras))
        enriched.stack = error.stack
        report(enriched, networkErrorKind)
    },
    info: (message: string): void => webLogger.info(message),
    setUser: (user: TLoggerUser): void => {
        loggerContext.user = user
    },
    removeUser: (): void => {
        loggerContext.user = null
    },
}
